import {
  Button,
  DatePicker,
  DefaultDateCell,
  Menu,
  type MenuRef,
  PropertyValue,
  Switch,
} from '@affine/component';
import type { FilterParams } from '@affine/core/modules/collection-rules';
import { i18nTime, useI18n } from '@affine/i18n';
import { DateTimeIcon } from '@blocksuite/icons/rc';
import { cssVarV2 } from '@toeverything/theme/v2';
import type { DateCell } from '@affine/component';
import dayjs from 'dayjs';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { PlainTextDocGroupHeader } from '../explorer/docs-view/group-header';
import { StackProperty } from '../explorer/docs-view/stack-property';
import type { DocListPropertyProps, GroupHeaderProps } from '../explorer/types';
import { FilterValueMenu } from '../filter/filter-value-menu';
import { FilterOptionsGroup } from '../filter/options';
import type { PropertyValueProps } from '../properties/types';
import * as styles from './date.css';
import { formatDateValue, parseDateValue } from './date-value';

const useParsedDate = (value?: string) => {
  const t = useI18n();
  const parts = parseDateValue(value);

  let displayValue = undefined;
  if (parts) {
    const accuracy = parts.hasTime ? 'minute' : 'day';
    const startStr = i18nTime(parts.start.toISOString(), {
      absolute: { accuracy },
    });
    if (parts.end) {
      const endStr = i18nTime(parts.end.toISOString(), {
        absolute: { accuracy },
      });
      displayValue = `${startStr} - ${endStr}`;
    } else {
      displayValue = startStr;
    }
  }

  return {
    parts,
    displayValue:
      displayValue ??
      t['com.affine.page-properties.property-value-placeholder'](),
  };
};

const DateEditor = ({
  value,
  onChange,
  onClose,
}: {
  value?: string;
  onChange: (v?: string) => void;
  onClose: () => void;
}) => {
  const parts = parseDateValue(value) ?? { start: dayjs(), hasTime: false };
  const t = useI18n();
  // values are committed only after the user actually touches a control —
  // otherwise opening the editor on an empty property would silently write
  // today's date
  const dirty = useRef(false);
  const [start, setStart] = useState<string>(parts.start.format('YYYY-MM-DD'));
  const [end, setEnd] = useState<string | undefined>(
    parts.end?.format('YYYY-MM-DD')
  );
  const [pickingEnd, setPickingEnd] = useState(false);
  const [hasTime, setHasTime] = useState(parts.hasTime);
  const [startTime, setStartTime] = useState<string>(
    parts.hasTime ? parts.start.format('HH:mm') : '00:00'
  );
  const [endTime, setEndTime] = useState<string>(
    parts.hasTime && parts.end ? parts.end.format('HH:mm') : '00:00'
  );

  useEffect(() => {
    if (!dirty.current) return;
    const startDay = dayjs(start + (hasTime ? 'T' + startTime : ''));
    if (!startDay.isValid()) return;

    let endDay: dayjs.Dayjs | undefined;
    if (end) {
      endDay = dayjs(end + (hasTime ? 'T' + endTime : ''));
      if (!endDay.isValid()) return;
    }

    onChange(formatDateValue({ start: startDay, end: endDay, hasTime }));
  }, [start, end, hasTime, startTime, endTime, onChange]);

  // Two-click range selection:
  // 1st click sets the start date, 2nd click sets the end date (auto-swapped
  // if the clicked date is before the start). Clicking the start date again
  // collapses the range to a single day. Once a range exists, a new click
  // starts a fresh range.
  const handleDateClick = (v: string) => {
    dirty.current = true;
    if (pickingEnd) {
      setPickingEnd(false);
      if (v === start) {
        setEnd(undefined);
        return;
      }
      if (dayjs(v).isBefore(dayjs(start), 'day')) {
        setEnd(start);
        setStart(v);
      } else {
        setEnd(v);
      }
    } else {
      setPickingEnd(true);
      setStart(v);
      setEnd(undefined);
    }
  };

  const renderRangeDay = (cell: DateCell) => {
    const dateStr = cell.date.format('YYYY-MM-DD');
    const isStart = dateStr === start;
    const isEnd = !!end && dateStr === end;
    const inRange =
      !!end &&
      cell.date.isAfter(dayjs(start), 'day') &&
      cell.date.isBefore(dayjs(end), 'day');
    if (isStart || isEnd) {
      return <DefaultDateCell {...cell} selected />;
    }
    if (inRange) {
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 8,
            backgroundColor: 'var(--affine-hover-color)',
          }}
        >
          <DefaultDateCell {...cell} />
        </div>
      );
    }
    return <DefaultDateCell {...cell} />;
  };

  return (
    <div
      style={{
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        minWidth: 260,
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: 'var(--affine-text-secondary-color)',
        }}
      >
        {end || !pickingEnd
          ? 'Select a date range'
          : 'Select end date (or click start again for a single day)'}
      </div>
      <DatePicker
        key={pickingEnd ? start : `${start}-${end ?? ''}`}
        value={start}
        onChange={handleDateClick}
        customDayRenderer={renderRangeDay}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontSize: 14 }}>
          {t['com.affine.dateProperty.includeTime']()}
        </span>
        <Switch
          checked={hasTime}
          onChange={v => {
            dirty.current = true;
            setHasTime(v);
          }}
        />
      </div>

      {hasTime && (
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 12,
                color: 'var(--affine-text-secondary-color)',
                marginBottom: 4,
              }}
            >
              {t['com.affine.dateProperty.start-time']()}
            </div>
            <input
              type="time"
              value={startTime}
              onChange={e => {
                dirty.current = true;
                setStartTime(e.target.value);
              }}
              style={{
                width: '100%',
                border: '1px solid var(--affine-border-color)',
                padding: '4px 8px',
                borderRadius: 4,
              }}
            />
          </div>
          {end && (
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--affine-text-secondary-color)',
                  marginBottom: 4,
                }}
              >
                {t['com.affine.dateProperty.end-time']()}
              </div>
              <input
                type="time"
                value={endTime}
                onChange={e => {
                  dirty.current = true;
                  setEndTime(e.target.value);
                }}
                style={{
                  width: '100%',
                  border: '1px solid var(--affine-border-color)',
                  padding: '4px 8px',
                  borderRadius: 4,
                }}
              />
            </div>
          )}
        </div>
      )}

      <div
        style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}
      >
        <Button
          onClick={() => {
            onChange(undefined);
            onClose();
          }}
        >
          {t['com.affine.button.clear']()}
        </Button>
      </div>
    </div>
  );
};

export const DateValue = ({
  value,
  onChange,
  readonly,
}: PropertyValueProps) => {
  const { parts, displayValue } = useParsedDate(value as string | undefined);
  const [open, setOpen] = useState(false);

  if (readonly) {
    return (
      <PropertyValue
        className={parts ? '' : styles.empty}
        isEmpty={!parts}
        readonly
      >
        {displayValue}
      </PropertyValue>
    );
  }

  return (
    <Menu
      rootOptions={{ open, onOpenChange: setOpen }}
      contentOptions={{
        style: BUILD_CONFIG.isMobileEdition ? { padding: '15px 20px' } : {},
      }}
      items={
        <DateEditor
          value={value as string | undefined}
          onChange={onChange}
          onClose={() => setOpen(false)}
        />
      }
    >
      <PropertyValue className={parts ? '' : styles.empty} isEmpty={!parts}>
        {displayValue}
      </PropertyValue>
    </Menu>
  );
};

const DateSelectorMenu = ({
  ref,
  value,
  onChange,
  onClose,
}: {
  ref?: React.Ref<MenuRef>;
  value?: string;
  onChange: (value: string) => void;
  onClose?: () => void;
}) => {
  const t = useI18n();
  const [open, setOpen] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      changeOpen: (open: boolean) => {
        setOpen(open);
        if (!open) {
          onClose?.();
        }
      },
    }),
    [onClose]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open);
      if (!open) {
        onClose?.();
      }
    },
    [onClose]
  );

  const handleChange = useCallback(
    (value: string) => {
      onChange(value);
      setOpen(false);
      onClose?.();
    },
    [onChange, onClose]
  );

  return (
    <FilterValueMenu
      rootOptions={{
        open,
        onOpenChange: handleOpenChange,
      }}
      contentOptions={{
        style: { padding: '12px 16px' },
      }}
      items={<DatePicker value={value || undefined} onChange={handleChange} />}
    >
      {value ? (
        <span>{value}</span>
      ) : (
        <span style={{ color: cssVarV2('text/placeholder') }}>
          {t['com.affine.filter.empty']()}
        </span>
      )}
    </FilterValueMenu>
  );
};

const DateFilterValueAfterBefore = ({
  filter,
  isDraft,
  onDraftCompleted,
  onChange,
}: {
  filter: FilterParams;
  isDraft?: boolean;
  onDraftCompleted?: () => void;
  onChange?: (filter: FilterParams) => void;
}) => {
  const menuRef = useRef<MenuRef>(null);
  const value = filter.value;
  const values = value?.split(',') ?? [];

  const handleChange = useCallback(
    (date: string) => {
      onChange?.({
        ...filter,
        value: date,
      });
    },
    [onChange, filter]
  );

  useEffect(() => {
    if (isDraft) {
      menuRef.current?.changeOpen(true);
    }
  }, [isDraft]);

  return (
    <DateSelectorMenu
      ref={menuRef}
      value={values[0]}
      onChange={handleChange}
      onClose={onDraftCompleted}
    />
  );
};

export const DateFilterValue = ({
  filter,
  isDraft,
  onDraftCompleted,
  onChange,
}: {
  filter: FilterParams;
  isDraft?: boolean;
  onDraftCompleted?: () => void;
  onChange?: (filter: FilterParams) => void;
}) => {
  const value = filter.value;
  const values = value?.split(',') ?? [];

  const handleChange = useCallback(
    (date: string) => {
      onChange?.({
        ...filter,
        value: date,
      });
    },
    [onChange, filter]
  );

  useEffect(() => {
    if (
      isDraft &&
      filter.method !== 'after' &&
      filter.method !== 'before' &&
      filter.method !== 'between'
    ) {
      onDraftCompleted?.();
    }
  }, [isDraft, filter.method, onDraftCompleted]);

  return filter.method === 'after' || filter.method === 'before' ? (
    <DateFilterValueAfterBefore
      filter={filter}
      isDraft={isDraft}
      onDraftCompleted={onDraftCompleted}
      onChange={onChange}
    />
  ) : filter.method === 'between' ? (
    <FilterOptionsGroup
      isDraft={isDraft}
      onDraftCompleted={onDraftCompleted}
      items={[
        ({ onDraftCompleted, menuRef }) => (
          <DateSelectorMenu
            ref={menuRef}
            value={values[0]}
            onChange={value => handleChange(`${value},${values[1] || ''}`)}
            onClose={onDraftCompleted}
          />
        ),
        <span key="between" style={{ color: cssVarV2('text/placeholder') }}>
          &nbsp;-&nbsp;
        </span>,
        ({ onDraftCompleted, menuRef }) => (
          <DateSelectorMenu
            ref={menuRef}
            value={values[1]}
            onChange={value => handleChange(`${values[0] || ''},${value}`)}
            onClose={onDraftCompleted}
          />
        ),
      ]}
    ></FilterOptionsGroup>
  ) : undefined;
};

export const DateDocListProperty = ({ value }: DocListPropertyProps) => {
  const { parts, displayValue } = useParsedDate(value as string | undefined);
  if (!parts) return null;

  return <StackProperty icon={<DateTimeIcon />}>{displayValue}</StackProperty>;
};

export const DateGroupHeader = ({ groupId, docCount }: GroupHeaderProps) => {
  // group ids are codec day keys ('YYYY-MM-DD') — show a localized date, not
  // the raw key
  const { parts, displayValue } = useParsedDate(groupId);

  return (
    <PlainTextDocGroupHeader groupId={groupId} docCount={docCount}>
      {parts ? displayValue : groupId || 'No Date'}
    </PlainTextDocGroupHeader>
  );
};
